import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MarketingLandingPage.css';

interface TeamMember {
  name: string;
  photo: string;
  linkedin: string;
  email: string;
}

const teamMembers: { [key: string]: TeamMember } = {
  hillel: {
    name: "Hillel Sztern",
    photo: "/assets/hillel.jpg",
    linkedin: "https://www.linkedin.com/in/hillel-sztern-870aa1268",
    email: "hillel.sztern@mail.huji.ac.il"
  },
  leehi: {
    name: "Leehi Pridan",
    photo: "/assets/leehi.jpg",
    linkedin: "https://www.linkedin.com/in/leehi-pridan-849116266",
    email: "Leehi.pridan@mail.huji.ac.il"
  },
  maayan: {
    name: "Maayan Aloni",
    photo: "/assets/maayan.jpg",
    linkedin: "https://www.linkedin.com/in/maayan-aloni",
    email: "Maayan.aloni@mail.huji.ac.il"
  },
  noa: {
    name: "Noa Shohat",
    photo: "/assets/noa.jpg",
    linkedin: "https://www.linkedin.com/in/noa-shohat-0aa5a32b3",
    email: "Noa.shohat@mail.huji.ac.il"
  },
  shani: {
    name: "Shani Erez",
    photo: "/assets/shani.jpg",
    linkedin: "https://www.linkedin.com/in/shani-erez-b03b35240",
    email: "Shani.erez@mail.huji.ac.il"
  },
  sivan: {
    name: "Sivan Kijner",
    photo: "/assets/sivan.jpg",
    linkedin: "https://www.linkedin.com/in/sivan-kijner",
    email: "sivan.kijner@mail.huji.ac.il"
  }
};

const MarketingLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', hospital: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDemoClick = () => {
    navigate('/dashboard');
  };

  const openDemoPopup = () => {
    setShowDemoPopup(true);
    setFormData({ name: '', email: '', hospital: '' });
    setShowSuccess(false);
  };

  const closeDemoPopup = () => {
    setShowDemoPopup(false);
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Formspree form endpoint
    const FORMSPREE_URL = 'https://formspree.io/f/xeoywepv';
    
    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          hospital: formData.hospital,
          _subject: `SkillScope Demo Request from ${formData.name}`
        })
      });

      const data = await response.json();
      console.log('Formspree response:', response.status, data);

      if (response.ok) {
        setShowSuccess(true);
        setIsSubmitting(false);
        setTimeout(() => {
          closeDemoPopup();
          setFormData({ name: '', email: '', hospital: '' });
        }, 2000);
      } else {
        console.error('Form submission failed:', data);
        alert('Submission failed: ' + (data.error || 'Please try again'));
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  const openTeamMember = (memberId: string) => {
    const member = teamMembers[memberId];
    if (member) {
      setSelectedMember(member);
      setShowTeamModal(true);
    }
  };

  const closeTeamMember = () => {
    setShowTeamModal(false);
    setSelectedMember(null);
  };

  return (
    <div className="marketing-page-new">
      {/* Hero Section */}
      <section className="w-full gradient-bg text-white py-24 px-6">
        <div className="max-w-6xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img 
              src="/assets/skillscope-logo.png" 
              alt="SkillScope Logo" 
              className="rounded-2xl"
              style={{ maxWidth: '600px', width: '100%' }}
            />
          </div>
          <h1 className="text-6xl font-bold mb-6">Transform Medical Training with AI</h1>
          <p className="text-2xl mb-10 opacity-90">Objective, real-time assessment of clinical performance using hospital data</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleDemoClick}
              className="bg-white text-blue-800 px-10 py-4 rounded-full text-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Started Today
            </button>
            <button 
              onClick={openDemoPopup}
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full text-xl font-semibold hover:bg-white hover:text-blue-800 transition-colors"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Demo Popup Modal */}
      {showDemoPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-6" onClick={closeDemoPopup}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Schedule a Demo at Your Hospital</h2>
            {!showSuccess ? (
              <form onSubmit={handleDemoSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    required
                    placeholder="Dr. John Smith"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-gray-900"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="your.email@hospital.com"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-gray-900"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">Hospital / Organization</label>
                  <input
                    type="text"
                    required
                    placeholder="Memorial Hospital"
                    value={formData.hospital}
                    onChange={e => setFormData({...formData, hospital: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-gray-900"
                  />
                </div>
                <div className="flex gap-3">
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-blue-700 to-blue-800 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-800 hover:to-blue-900 transition-colors"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeDemoPopup}
                    className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                <p className="text-green-800 font-medium text-center">✓ Thank you! We'll be in touch soon.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Problem Section */}
      <section className="w-full bg-black py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-white mb-4">Residency is a black box.</h2>
          <h2 className="text-5xl font-bold text-white mb-8">We want to open it.</h2>
          <div className="max-w-4xl mx-auto mb-12 bg-gradient-to-r from-gray-800 to-gray-900 border-2 border-yellow-500 rounded-2xl p-8 shadow-2xl">
            <p className="text-lg text-gray-200 leading-relaxed">
              At 150–200K USD per resident per year, hospitals invest heavily in residency training but still rely on non‑objective evaluations that do not deliver better outcomes, leaving patients, residents, and leadership paying the price.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 feature-card">
              <div className="mb-4">
                <svg className="w-12 h-12 text-blue-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2 text-xl">Sporadic Feedback</h3>
              <p className="text-gray-400">Delayed insights with no real-time tracking</p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 feature-card">
              <div className="mb-4">
                <svg className="w-12 h-12 text-yellow-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2 text-xl">Subjective Evaluations</h3>
              <p className="text-gray-400">No objective metrics for clinical performance</p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 feature-card">
              <div className="mb-4">
                <svg className="w-12 h-12 text-orange-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2 text-xl">Time-Consuming</h3>
              <p className="text-gray-400">Burden chiefs and seniors and delay feedback</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="w-full bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              OUR SOLUTION
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">AI-Powered Clinical Assessment Platform</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              SkillScope analyzes real hospital data to objectively evaluate resident performance, reconstructs clinical encounters, and provides personalized feedback mapped to ACGME competencies.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 feature-card shadow-lg">
              <div className="mb-4">
                <svg className="w-14 h-14 text-blue-700 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Objective Evaluation</h3>
              <p className="text-gray-600">Real-time assessment using de-identified hospital data with no manual input required</p>
            </div>
            <div className="bg-white rounded-xl p-8 feature-card shadow-lg">
              <div className="mb-4">
                <svg className="w-14 h-14 text-blue-700 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Progress Tracking</h3>
              <p className="text-gray-600">Maps performance to ACGME competencies and identifies experience gaps early</p>
            </div>
            <div className="bg-white rounded-xl p-8 feature-card shadow-lg">
              <div className="mb-4">
                <svg className="w-14 h-14 text-blue-700 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Personalized Insights</h3>
              <p className="text-gray-600">AI-driven case recommendations tailored to individual learning needs</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <button 
              onClick={handleDemoClick}
              className="bg-gradient-to-r from-blue-700 to-blue-800 text-white px-16 py-6 rounded-full text-2xl font-bold hover:from-blue-800 hover:to-blue-900 transition-all transform hover:scale-105 shadow-xl"
            >
              How It Works
            </button>
          </div>
        </div>
      </section>

      {/* Innovation Section */}
      <section className="w-full bg-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">What Makes Us Different</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-8 h-8 text-blue-700" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-900">Product Innovation</h3>
              </div>
              <p className="text-gray-700 mb-4">Unlike existing platforms, SkillScope analyzes real-time hospital data providing objective, dynamic feedback on both exposure and performance.</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><span className="mr-2">✓</span><span>Reduces administrative burden significantly</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>Links clinical exposure with competency development</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>Delivers actionable, personalized recommendations</span></li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-8 h-8 text-blue-700" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-900">Security &amp; Compliance</h3>
              </div>
              <p className="text-gray-700 mb-4">Built on de-identified MIMIC-III data with enterprise-grade security:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><span className="mr-2">✓</span><span>On-premise GPT-4.1 deployment</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>HIPAA and GDPR compliant</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>No cloud storage - secure local processing</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>Seamless hospital system integration</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About Us / Team */}
      <section className="w-full bg-gray-50 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-400 rounded-2xl px-8 py-6 mb-6 shadow-lg">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="text-6xl">🏆</div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-gray-900">Winner</div>
                  <div className="text-lg font-semibold text-yellow-700">Medical Education Category</div>
                </div>
              </div>
              <div className="text-lg font-semibold text-gray-800 mt-3">
                Mindset Hackathon for Medical Students 2025
              </div>
              <div className="mt-6 space-y-4">
                <img 
                  src="https://i.ibb.co/SDsdbxmX/IMG-4045.jpg" 
                  alt="Award Ceremony" 
                  className="rounded-xl shadow-lg mx-auto"
                  style={{ maxWidth: '600px', width: '100%' }}
                />
              </div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              We are a team of six final‑year medical students, soon to begin residency, dedicated to transforming medical education through innovative technology.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {Object.entries(teamMembers).map(([id, member]) => (
              <div 
                key={id}
                className="bg-white rounded-xl p-8 text-center team-card cursor-pointer shadow-lg"
                onClick={() => openTeamMember(id)}
              >
                <div className="w-32 h-32 rounded-full mx-auto mb-4 overflow-hidden">
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-blue-700 font-medium">Co-Founder</p>
              </div>
            ))}
          </div>

          {/* Team Member Profile Modal */}
          {showTeamModal && selectedMember && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-6"
              onClick={closeTeamMember}
            >
              <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="text-center">
                  <div className="w-48 h-48 rounded-full mx-auto mb-6 overflow-hidden">
                    <img 
                      src={selectedMember.photo} 
                      alt={selectedMember.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedMember.name}</h2>
                  <p className="text-blue-700 font-medium text-lg mb-6">Co-Founder</p>
                  <div className="space-y-3">
                    <a 
                      href={selectedMember.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                      Connect on LinkedIn
                    </a>
                    <a 
                      href={`mailto:${selectedMember.email}`}
                      className="flex items-center justify-center gap-3 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {selectedMember.email}
                    </a>
                  </div>
                  <button 
                    onClick={closeTeamMember}
                    className="mt-6 w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full gradient-bg text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Medical Training?</h2>
          <p className="text-xl mb-10 opacity-90">Join the leading hospitals leveraging AI for better resident outcomes</p>
          <button 
            onClick={openDemoPopup}
            className="bg-white text-blue-800 px-12 py-5 rounded-full text-xl font-semibold hover:bg-gray-100 transition-colors"
          >
            Schedule a Demo
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full bg-gray-900 text-gray-300 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-white mb-4">SKILLSCOPE</h3>
          <p className="mb-6">Empowering the next generation of healthcare professionals</p>
          <p className="text-sm text-gray-500">© 2024 SkillScope. All rights reserved. | HIPAA &amp; GDPR Compliant</p>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLandingPage;
